import { makeAutoObservable } from "mobx";
import { PModifierInstance, PProductUnavailabilityDetails } from ".";
import { naturalCompare, RArray, RSet } from "../collections";
import { POrderLineProductType } from "./POrderLineProductType";
export class POrderLine {
    constructor(params) {
        this.domain = params.domain;
        this.restaurant = params.restaurant;
        this.localizedPrice = params.localizedPrice;
        this.localizationProvider = params.localizationProvider;
        makeAutoObservable(this);
    }
    get id() {
        return this.domain.id;
    }
    get price() {
        var _a;
        return (_a = this.localizedPrice.mainPriceValue) !== null && _a !== void 0 ? _a : "0";
    }
    get alternativePrice() {
        return this.localizedPrice.alternativePriceValue;
    }
    get quantity() {
        return this.domain.quantity.value;
    }
    get name() {
        const name = this.parts.map((part) => part.name).join(" / ");
        if (this.quantity > 1) {
            const quantity = this.domain.quantity.toString();
            return `${quantity}x ${name}`;
        }
        return name;
    }
    get description() {
        const parameters = this.parameterNames(this.domain.productInstance.commonConfig.customParameters.choices);
        const nonDivisibleModifiersOrder = RSet.fromIterable(new RArray(this.domain.productInstance.productTypeIds.rawValues())
            .mapOptional((id) => this.restaurant.productTypes.find(id))
            .flatMap((productType) => new RArray(productType.nonDivisibleModifiersOrder)), { ignoreDuplicates: true }).rawValues();
        const modifiers = this.modifierNames(this.domain.productInstance.commonConfig.modifierInstances.objects, (a, b) => nonDivisibleModifiersOrder.indexOf(a) -
            nonDivisibleModifiersOrder.indexOf(b));
        return parameters.concat(modifiers).join(", ");
    }
    // TODO: proper item image src:
    get image() {
        var _a, _b;
        // CustomProductDetails {} -> Nothing
        // MenuProductDetails { parts } -> head $ catMaybes $ _.image <$> parts
        // MenuComboDetails elements -> head $ catMaybes $ fold $ (\{ parts } -> _.image <$> parts) <$> elements
        return (_b = (_a = this.parts[0]) === null || _a === void 0 ? void 0 : _a.photo) !== null && _b !== void 0 ? _b : null;
    }
    get availability() {
        return this.domain.productInstance.availability;
    }
    get error() {
        const { message } = new PProductUnavailabilityDetails({
            availability: this.availability,
            restaurant: this.restaurant,
        });
        return message;
    }
    get hasError() {
        return this.error !== null;
    }
    parameterNames(parameterMap) {
        return new RArray(parameterMap)
            .map(([parameterType, choiceId]) => {
            var _a, _b;
            return (_b = (_a = this.restaurant.customParameterTypes
                .get(parameterType.id)
                .choices.find((choice) => choice.id.eq(choiceId))) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : "empty";
        })
            .raw.sort(naturalCompare);
    }
    modifierNames(modifierInstances, comparator) {
        return modifierInstances
            .map((modifierInstance) => new PModifierInstance({
            domain: modifierInstance,
            modifierType: this.restaurant.modifierTypes.get(modifierInstance.typeId),
            localizationProvider: this.localizationProvider,
        }))
            .sorted((a, b) => comparator(a.modifierType.id, b.modifierType.id))
            .map((pModifierInstance) => pModifierInstance.selectedItemsDescription)
            .filtered((description) => description.trim() !== "").raw;
    }
    get parts() {
        return new RArray(this.domain.productInstance.parts.productInstanceParts).raw
            .map((productInstancePart) => {
            const productType = this.restaurant.productTypes.get(productInstancePart.productTypeId);
            const parameters = this.parameterNames(productInstancePart.productConfig.customParameters.choices);
            const divisibleModifiersOrder = productType.divisibleModifiersOrder;
            const modifiers = this.modifierNames(productInstancePart.productConfig.modifierInstances.objects, (a, b) => divisibleModifiersOrder.indexOf(a) -
                divisibleModifiersOrder.indexOf(b));
            const description = parameters.concat(modifiers).join(", ");
            return new POrderLineProductType({ productType, description });
        })
            .sort((a, b) => naturalCompare(a.name, b.name));
    }
    get isSinglePart() {
        return this.domain.productInstance.parts.type === "SinglePart";
    }
    increaseQuantity() {
        this.domain.increaseQuantity();
    }
    decreaseQuantity() {
        this.domain.decreaseQuantity();
    }
}
